import { useState, useEffect, useCallback } from 'react';
import {
  getTodayStatus,
  getAdminMonths,
  TodayStatus,
  AdminMonth,
} from '../api/adminApi';

// ─── Action types ─────────────────────────────────────────────────────────────

/** Month start date not yet recorded — admin must enter it */
export interface ConfirmStartAction {
  type: 'CONFIRM_START';
  month: AdminMonth;
}

/** Day 29 and sighting result unknown — admin must record tonight's result */
export interface SightingNightAction {
  type: 'SIGHTING_NIGHT';
  currentMonth: AdminMonth;
  nextMonth: AdminMonth | null;
}

export type AdminAction = ConfirmStartAction | SightingNightAction | null;

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface CountryState {
  status: TodayStatus | null;
  calcStatus: TodayStatus | null;
  months: AdminMonth[];
  isInitialized: boolean;
  action: AdminAction;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function deriveAction(
  status: TodayStatus,
  calcStatus: TodayStatus | null,
  months: AdminMonth[]
): AdminAction {
  const day = status.date.day;

  if (day === 0 || status.date.month.index === 0) {
    const ref = calcStatus ?? status;
    const pending = months.find(
      (m) =>
        m.year === ref.date.year &&
        m.monthIndex === ref.date.month.index &&
        m.status === 'PENDING'
    );
    if (pending) return { type: 'CONFIRM_START', month: pending };
    return null;
  }

  if (day === 29 && !status.sightingResult) {
    const currentMonth =
      months.find(
        (m) => m.year === status.date.year && m.monthIndex === status.date.month.index
      ) ?? null;

    const nextMonthIndex = status.date.month.index === 12 ? 1 : status.date.month.index + 1;
    const nextMonthYear =
      status.date.month.index === 12 ? status.date.year + 1 : status.date.year;
    const nextMonth =
      months.find((m) => m.year === nextMonthYear && m.monthIndex === nextMonthIndex) ?? null;

    if (currentMonth) {
      return { type: 'SIGHTING_NIGHT', currentMonth, nextMonth };
    }
  }

  return null;
}

export function useCountry(locationCode: string): CountryState {
  const [status, setStatus] = useState<TodayStatus | null>(null);
  const [calcStatus, setCalcStatus] = useState<TodayStatus | null>(null);
  const [months, setMonths] = useState<AdminMonth[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [locStatus, monthList] = await Promise.all([
        getTodayStatus(locationCode),
        getAdminMonths(locationCode),
      ]);
      setStatus(locStatus);
      setMonths(monthList);

      // When date is unknown, fetch SA (calculation) as reference
      if (locStatus.date.day === 0 || locStatus.date.month.index === 0) {
        const sa = await getTodayStatus('SA').catch(() => null);
        setCalcStatus(sa);
      } else {
        setCalcStatus(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [locationCode]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const isInitialized = months.some((m) => m.status === 'CONFIRMED');
  const action = status && !loading ? deriveAction(status, calcStatus, months) : null;

  return { status, calcStatus, months, isInitialized, action, loading, error, refetch: fetchAll };
}
